<script lang="ts">
    import { onMount } from 'svelte';
    import { closeModal, onBeforeClose } from 'svelte-modals';
    import { _ } from 'svelte-i18n';
    import IconClose from '../Icons/IconClose.svelte';
    import Select from '../Select.svelte';
    import SearchInput from '../SearchInput.svelte';
    import {
        guide,
        guidePageContext,
        guideSearchLiveQuery,
        guideSearchQuery,
        clearGuideSearch,
        resetDocumentation,
        selectChapter,
    } from '../../components/Guide/guideStore';
    import { GuideApi } from '../../components/Guide/GuideApi';
    import { debug } from '../../../stores/store';
    import { api } from '../../../stores/api';

    export let isOpen: boolean = false;
    export let component: any;
    export let componentProps: any = {};
    export let origin: Element|null = null;

    export let onClick: () => void = () => {};

    let modal: Element;
    let closing: boolean = false;

    // chapter titles come from BookStack, so they are ready-made text rather than locale keys
    $: chapterItems = $guide.chapters.map(chapter => ({ id: chapter.key, label: chapter.title }));

    // every open starts with an empty search (unlike the remembered page/scroll)
    onMount(() => {
        if (origin) {
            //await new Promise(requestAnimationFrame);

            const from = origin.getBoundingClientRect();
            const to = modal.getBoundingClientRect();

            const dx = from.left - to.left;
            const dy = from.top - to.top;
            const sx = from.width / to.width;
            const sy = from.height / to.height;

            modal.animate(
                [
                    {
                        transformOrigin: 'top left',
                        transform: `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`
                    },
                    {
                        transformOrigin: 'top left',
                        transform: 'none'
                    }
                ],
                {
                    duration: 300,
                    easing: 'cubic-bezier(.2,.8,.2,1)'
                }
            );
        }
        clearGuideSearch();
    });

    onBeforeClose(() => {
        if (closing) {
            return true;
        } else {
            closeWithAnimation();
            return false;
        }
    })

    async function closeWithAnimation() {
        if (origin) {
            if (closing) return;

            closing = true;

            const from = modal.getBoundingClientRect();
            const to = origin.getBoundingClientRect();

            const dx = to.left - from.left;
            const dy = to.top - from.top;
            const sx = to.width / from.width;
            const sy = to.height / from.height;

            const animation = modal.animate(
                [
                    {
                        transformOrigin: 'top left',
                        transform: 'none'
                    },
                    {
                        transformOrigin: 'top left',
                        transform: `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`
                    }
                ],
                {
                    duration: 300,
                    easing: 'cubic-bezier(.4,0,1,1)'
                }
            );

            await animation.finished;
        }

        close();
    }

    function close(): void {
        closeModal();
        onClick();
    }

    /** Debug-only: clear the server cache, drop the loaded documentation and close the modal.
     * Reopening it re-fetches from BookStack and shows the spinner, so the fresh content loads. */
    async function resetCache(): Promise<void> {
        try {
            await GuideApi.resetCache($api);
        } catch (e) {
            console.error('Guide, resetCache', e);
        }
        resetDocumentation();
        close();
    }
</script>

{#if isOpen}
    <div role="dialog" class="modal" bind:this={modal}>
        <div class="contents">
            <header class="header">
                <div class="heading">
                    <h3 class="title">{$_('documentation')}</h3>
                    {#if chapterItems.length > 1}
                        <Select
                            items={chapterItems}
                            selected={$guide.selectedChapterKey}
                            translateLabels={false}
                            onChange={key => {
                                selectChapter(key);
                                return true;
                            }}
                        />
                    {/if}
                </div>
                <div class="menu-search">
                    <SearchInput
                        bind:query={$guideSearchLiveQuery}
                        placeholder={$_('documentation_search_placeholder')}
                        onKeyup={value => guideSearchQuery.set(value)}
                        onChange={value => guideSearchQuery.set(value)}
                    />
                </div>
                {#if $debug}
                    <!-- the slug a page has to register as its currentPage - read it here, set it in onMount -->
                    <div class="debug">
                        <span>currentPage: <b>{$guide.selectedPageSlug || '-'}</b></span>
                        <span>kontekst strony: <b>{$guidePageContext || 'brak'}</b></span>
                    </div>
                    <button type="button" class="reset-cache" on:click={resetCache}>
                        {$_('documentation_reset_cache')}
                    </button>
                {/if}
                <IconClose scale={0.7} title={$_('Close')} on:click={closeWithAnimation} />
            </header>
            <div class="body">
                <svelte:component this={component} {componentProps} />
            </div>
        </div>
    </div>
{/if}

<style>
    .modal {
        position: fixed;
        top: 0;
        bottom: 0;
        right: 0;
        left: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 1002;

        /* allow click-through to backdrop */
        pointer-events: none;
    }

    .contents {
        width: 1100px;
        height: 680px;
        max-width: 95vw;
        max-height: 95vh;
        border-radius: 6px;
        background: white;
        display: flex;
        flex-direction: column;
        pointer-events: auto;
        font-size: 14px;
        padding: 0;
        overflow: hidden;
        box-shadow:
            0 0 0 10px #1c5dc722,
            0 4px 8px rgba(0, 0, 0, 0.2);
    }

    .header {
        position: relative;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        column-gap: 15px;
        /*background-color: var(--automapa-blue, #1c5dc7);*/
        background-color: #1c5dc7;
        color: white;
        padding: 15px 15px 15px 26px;
        border-top-left-radius: 4px;
        border-top-right-radius: 4px;
    }

    .heading {
        display: flex;
        flex-direction: row;
        align-items: center;
        column-gap: 20px;
        min-width: 0;
    }

    .title {
        margin: 0;
        text-align: left;
        white-space: nowrap;
    }

    /* centred across the whole header, with room on both sides regardless of the left/right content */
    .menu-search {
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        width: 320px;
        max-width: 40%;
    }

    .debug {
        display: flex;
        flex-direction: column;
        row-gap: 2px;
        margin-left: auto;
        padding-right: 10px;
        font-size: 11px;
        font-family: monospace;
        color: #ffffffcc;
        user-select: text;
    }

    .reset-cache {
        flex: 0 0 auto;
        border: 1px solid #ffffff66;
        border-radius: 6px;
        background-color: #ffffff1f;
        color: #fff;
        padding: 5px 10px;
        font-size: 12px;
        font-weight: 600;
        cursor: pointer;
        white-space: nowrap;
    }

    .reset-cache:hover {
        background-color: #ffffff33;
    }

    /* the shared Select is styled for light backgrounds; the header is blue */
    .heading :global(select) {
        max-width: 320px;
        border: 1px solid #ffffff66;
        border-radius: 6px;
        background-color: #ffffff1f;
        color: #fff;
        padding: 5px 8px;
        font-size: 13px;
        font-weight: 600;
    }

    .heading :global(select option) {
        color: #333;
        background-color: #fff;
    }

    .header :global(img) {
        /* the icon is black by default */
        /*filter: invert(1);*/
    }

    .body {
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        min-height: 0;
    }
</style>
